import React, { useEffect, useState } from "react";
import "./styles.scss";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getArticle } from "../../actions/news";
import { slugify, toLangPath } from "../../utilities/helpers";
import moment from "moment";
import { ImageGallery } from "../../components/ImageGallery";
import Popup from "reactjs-popup";
import { FacebookShareButton, FacebookIcon, TwitterShareButton, TwitterIcon, ViberShareButton, ViberIcon, EmailShareButton, EmailIcon, LinkedinShareButton, LinkedinIcon } from "react-share";
import SEO from "../../components/SEO/SEO";
import { SITE_URL, SITE_NAME, DEFAULT_IMAGE } from "../../config/seo";

const Article = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id, slug } = useParams();
  const language = useSelector((state) => state.general.language);

  const [article, setArticle] = useState();

  useEffect(() => {
    if (id && !article) dispatch(getArticle({ id, onSuccess: setArticle }));
  }, [article, dispatch, id]);

  // After fetching, normalize URL to canonical /article/:slug/:id
  useEffect(() => {
    if (!article || !id) return;
    const title = language === "en" ? (article.titleEnglish || article.title) : (article.title || article.titleEnglish);
    const targetSlug = slugify(title || "");
    if (!targetSlug) return; // nothing to normalize if title missing
    // If no slug in URL, or slug mismatch, redirect to canonical
    if (!slug || slug !== targetSlug) {
      const path = `/article/${targetSlug}/${id}`;
      const lang = language === 'en' ? 'en' : 'bg';
      navigate(toLangPath(path, lang), { replace: true });
    }
  }, [article, id, language, navigate, slug]);

  const [selectedImage, setSelectedImage] = useState(null);

  const articleTitle = language === "en" ? article?.titleEnglish || article?.title : article?.title || article?.titleEnglish;
  const articleBody = language === "en" ? article?.bodyEnglish || article?.body : article?.body || article?.bodyEnglish;
  // hreflang for an article cannot be derived by prefixing /en: each language
  // has its own slug. Encode the slug so these match the sitemap and the
  // canonical the browser reports for the same page.
  const slugFor = (value) => encodeURIComponent(slugify(value || ""));
  const bgSlug = slugFor(article?.title || article?.titleEnglish);
  const enSlug = slugFor(article?.titleEnglish || article?.title);
  const alternates =
    article && id && bgSlug && enSlug
      ? { bg: `/article/${bgSlug}/${id}`, en: `/en/article/${enSlug}/${id}` }
      : undefined;

  // Editors author the body as HTML; strip it down to plain text for the meta description.
  const articleDescription = articleBody
    ? articleBody
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/\s+/g, " ")
        .trim()
    : undefined;

  return (
    <>
      <SEO
        title={articleTitle ? `${articleTitle} | ${SITE_NAME}` : undefined}
        name={articleTitle}
        image={article?.mainPhoto}
        description={articleDescription}
        type={"article"}
        publishedTime={article?.createdAt}
        modifiedTime={article?.updatedAt || article?.createdAt}
        alternates={alternates}
        breadcrumbs={[
          { name: SITE_NAME, url: "/" },
          { name: t("general.headerUsefulInfo"), url: "/info" },
          ...(articleTitle ? [{ name: articleTitle, url: window.location.pathname }] : []),
        ]}
        jsonLd={
          article
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "NewsArticle",
                  headline: articleTitle || undefined,
                  description: articleDescription?.slice(0, 300) || undefined,
                  inLanguage: language === "en" ? "en" : "bg",
                  image: article.mainPhoto ? [article.mainPhoto, ...(article.photos ?? [])].filter(Boolean) : [DEFAULT_IMAGE],
                  datePublished: article.createdAt || undefined,
                  dateModified: article.updatedAt || article.createdAt || undefined,
                  articleSection: language === "en" ? article.category?.nameEnglish : article.category?.name,
                  author: article.author
                    ? { "@type": "Person", name: article.author }
                    : { "@type": "Organization", name: SITE_NAME, url: `${SITE_URL}/` },
                  publisher: { "@id": `${SITE_URL}/#organization` },
                  mainEntityOfPage: {
                    "@type": "WebPage",
                    "@id": `${SITE_URL}${window.location.pathname}`,
                  },
                },
              ]
            : []
        }
      />
      <div className="screen-article-container">
        <div className="screen-article-back" onClick={() => navigate(-1)}>
          <img src={require("../../assets/icons/arrow-orange.svg").default} className="icon-arrow" alt="back button" />
          <span>{t("general.back")}</span>
        </div>
        <div className="screen-article-header">
          {article?.category && <span className="category">{language === "en" ? article?.category?.nameEnglish : article?.category?.name}</span>}
          <h1>{articleTitle}</h1>
        </div>
        <div className="screen-article-share">
          <span>{article ? moment(article.createdAt).format("DD.MM.YYYY | HH:mm") : null}</span>
          <div className="icons-container">
            <FacebookShareButton url={window.location.href}>
              <FacebookIcon size={35} round={true} />
            </FacebookShareButton>
            <TwitterShareButton url={window.location.href}>
              <TwitterIcon size={35} round={true} />
            </TwitterShareButton>
            <ViberShareButton url={window.location.href}>
              <ViberIcon size={35} round={true} />
            </ViberShareButton>
            <EmailShareButton url={window.location.href}>
              <EmailIcon size={35} round={true} />
            </EmailShareButton>
            <LinkedinShareButton url={window.location.href}>
              <LinkedinIcon size={35} round={true} />
            </LinkedinShareButton>
          </div>
        </div>
        {article?.mainPhoto && (
          <div className="screen-article-main-photo">
            <img src={article?.mainPhoto} alt={articleTitle || "article"} loading="eager" onClick={() => setSelectedImage(0)} />
          </div>
        )}
        <div className="screen-article-gallery">
          {article?.photos?.map((el, i) => (
            <img key={el} src={el} alt={articleTitle ? `${articleTitle} — ${i + 1}` : `gallery-${i + 1}`} loading="lazy" onClick={() => setSelectedImage(i + 1)} />
          ))}
        </div>
        <div className="screen-article-body" dangerouslySetInnerHTML={{ __html: language === "en" ? article?.bodyEnglish : article?.body }} />
        <Popup modal lockScroll overlayStyle={{ zIndex: 100, background: `rgba(0, 0, 0, 0.9)` }} contentStyle={{ width: "auto" }} open={selectedImage !== null} onClose={() => setSelectedImage(null)}>
          <ImageGallery photos={[article?.mainPhoto, ...(article?.photos ?? [])].filter(Boolean)} selectedImage={selectedImage} close={() => setSelectedImage(null)} />
        </Popup>
      </div>
    </>
  );
};

export default Article;
